import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { getStockTransfers, receiveStockTransfer } from "../../components/Api/GmsStockTransfer";
import { getBranches } from "../../components/Api/GmsBranch";
import { Package, CheckCircle } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import StockDetailsModal from "../../components/StockDetailsModal";

// Custom styles for Select components (similar to PayrollProcessing)
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    branchId: user.user_Branch_Id || null,
  };
};

const emptyFilter = {
  StockTransfer_Id: null,
  StockTransfer_TransferNo: null,
  FromBranch_Id: null,
  ToBranch_Id: null,
  Business_Id: null,
  IsReceived: null,
  FromDate: null,
  ToDate: null,
};

export default function ReceiveStockTransferPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsReceiveStockTransfer');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsReceiveStockTransfer');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsReceiveStockTransfer');
  const isAdmin = isUserAdmin();

  const session = getUserSession();

  const [stockTransfers, setStockTransfers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState(emptyFilter);
  const [selectedBranchId, setSelectedBranchId] = useState(session.branchId);
  const [isBranchReadonly, setIsBranchReadonly] = useState(!!session.branchId);

  const [loading, setLoading] = useState(false);
  const [receiveLoadingRow, setReceiveLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1"); // 1 = Pending, 2 = Received
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentTransfer, setCurrentTransfer] = useState(null);
  const [showStockDetailsModal, setShowStockDetailsModal] = useState(false);
  const [currentStockDetailsProduct, setCurrentStockDetailsProduct] = useState('');
  const [currentStockDetails, setCurrentStockDetails] = useState([]);

  // Load branches
  useEffect(() => {
    loadBranches();
  }, []);

  // Load transfers when branch changes or tab changes
  useEffect(() => {
    if (selectedBranchId) {
      loadStockTransfers();
    }
  }, [selectedBranchId, activeTab, page]);

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadStockTransfers = async () => {
    if (!selectedBranchId) {
      setStockTransfers([]);
      setTotalCount(0);
      return;
    }

    setLoading(true);
    try {
      const filterData = {
        ...filter,
        ToBranch_Id: selectedBranchId,
        IsReceived: activeTab === "1" ? false : true, // Pending = false, Received = true
        FromDate: filter.FromDate,
        ToDate: filter.ToDate,
        StockTransfer_TransferNo: filter.StockTransfer_TransferNo || null,
        FromBranch_Id: filter.FromBranch_Id || null,
      };

      const data = await getStockTransfers(filterData);
      const transfers = Array.isArray(data) ? data : (data.data || []);
      setStockTransfers(transfers);
      setTotalCount(transfers.length);
    } catch (error) {
      console.error("Error loading stock transfers:", error);
      Swal.fire("Error", "Failed to load stock transfers: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBranchChange = (option) => {
    const branchId = option ? option.value : null;
    setSelectedBranchId(branchId);
    setPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadStockTransfers();
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setPage(1);
    loadStockTransfers();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
  };

  // Get paginated data
  const getPaginatedTransfers = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return stockTransfers.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // Branch options
  const branchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim()
    }));
  }, [branches]);

  // From Branch options for filter
  const fromBranchOptions = useMemo(() => {
    return branches.map(b => ({
      value: b.branch_Id,
      label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim()
    }));
  }, [branches]);

  const handleReceive = async (transfer) => {
    // Confirmation
    const result = await Swal.fire({
      title: 'Confirm Receive',
      text: `Are you sure you want to receive transfer ${transfer.stockTransfer_TransferNo}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#556ee6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Receive',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    setReceiveLoadingRow(transfer.stockTransfer_Id);

    try {
      const session = getUserSession();

      // Prepare receive request - auto-receive full quantity
      const receiveRequest = {
        StockTransfer_Id: transfer.stockTransfer_Id,
        ReceiveUserId: session.userId,
        ReceivedDate: new Date(),
        ReceivedTotalQty: transfer.stockTransfer_TotalQty || 0,
        DetailReceivedQty: (transfer.details || []).map(detail => ({
          StockTransferDet_Id: detail.stockTransferDet_Id,
          ReceivedQty: detail.stockTransferDet_Quantity || 0 // Full quantity
        }))
      };

      const result = await receiveStockTransfer(receiveRequest);

      console.log('=== RECEIVE RESULT ===');
      console.log('Result:', result);
      console.log('Result type:', typeof result);
      console.log('Result keys:', Object.keys(result || {}));

      // Handle both camelCase and PascalCase response formats, and tuple format
      const isSuccess = result?.isSuccess ?? result?.IsSuccess ?? result?.Item3 ?? false;
      const message = result?.message ?? result?.Message ?? result?.Item2 ?? "Stock transfer received successfully";
      const returnedId = result?.returnedId ?? result?.ReturnedId ?? result?.Item1 ?? 0;

      console.log('Parsed values - isSuccess:', isSuccess, 'message:', message, 'returnedId:', returnedId);

      // If response is empty/null but no error was thrown, assume success (API succeeded)
      // Check if returnedId > 0 OR isSuccess is true OR response exists but is empty object
      const responseIsEmpty = !result || (typeof result === 'object' && Object.keys(result).length === 0);
      const hasReturnedId = returnedId > 0;

      if (isSuccess || hasReturnedId || responseIsEmpty) {
        // If we reach here without error, it means the API call succeeded
        // Even if response is empty, if no error was thrown and we have a valid request, assume success
        Swal.fire("Success", message || "Stock transfer received successfully", "success");
        // Reload transfers
        setTimeout(() => {
          loadStockTransfers();
        }, 500); // Small delay to ensure DB update is complete
      } else {
        Swal.fire("Error", message || "Failed to receive stock transfer", "error");
      }
    } catch (error) {
      console.error("Error receiving stock transfer:", error);
      Swal.fire("Error", error.response?.data?.message || error.message || "Failed to receive stock transfer", "error");
    } finally {
      setReceiveLoadingRow(null);
    }
  };

  const handleViewDetails = async (transfer) => {
    try {
      // Load full transfer details
      const filter = { StockTransfer_Id: transfer.stockTransfer_Id };
      const data = await getStockTransfers(filter);
      const transferData = Array.isArray(data) ? data[0] : (data.data || [])[0];

      if (transferData) {
        setCurrentTransfer(transferData);
        setShowDetailsModal(true);
      } else {
        Swal.fire("Error", "Transfer details not found", "error");
      }
    } catch (error) {
      console.error("Error loading transfer details:", error);
      Swal.fire("Error", "Failed to load transfer details", "error");
    }
  };

  const handleViewStockDetails = (productName, stockDetails) => {
    setCurrentStockDetailsProduct(productName);
    setCurrentStockDetails(stockDetails || []);
    setShowStockDetailsModal(true);
  };

  // Don't render if no permission
  if (!hasPermission) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody className="text-center py-5">
                  <h4>Access Denied</h4>
                  <p>You don't have permission to view this page.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const breadcrumbItem = activeTab === "1" ? "Pending Receipts" : "Received";

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Receive Stock Transfer" breadcrumbItem={breadcrumbItem} />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "1" ? "active" : ""}
                      onClick={() => handleTabChange("1")}
                    >
                      <i className="bx bx-time"></i>
                      <span>Pending Receipts</span>
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      style={{
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => handleTabChange("2")}
                    >
                      <i className="bx bx-check-circle"></i>
                      <span>Received</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      {/* Branch Selection */}
                      <Row className="mb-4">
                        <Col md={4}>
                          <div className="mb-3">
                            <Label className="fw-bold">Branch <span className="text-danger">*</span></Label>
                            <Select
                              value={selectedBranchId ? branchOptions.find(opt => opt.value === selectedBranchId) : null}
                              onChange={handleBranchChange}
                              options={branchOptions}
                              isClearable={!isBranchReadonly}
                              isDisabled={isBranchReadonly}
                              placeholder="Select Branch..."
                              isSearchable
                              styles={selectStyles}
                              maxMenuHeight={200}
                            />
                            {isBranchReadonly && (
                              <small className="text-muted">Branch is pre-filled from your login</small>
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* Filters - Commented out for now */}
                      {/* <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Filter Purchase</h5>
                            <Button
                              color="primary"
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>

                          {showFilters && (
                            <>
                              <Row className="mb-3">
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>Transfer Number</Label>
                                    <Input
                                      type="text"
                                      value={filter.StockTransfer_TransferNo || ''}
                                      onChange={(e) => handleFilterChange('StockTransfer_TransferNo', e.target.value || null)}
                                      placeholder="Enter transfer number..."
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>From Branch</Label>
                                    <Select
                                      value={filter.FromBranch_Id ? fromBranchOptions.find(opt => opt.value === filter.FromBranch_Id) : null}
                                      onChange={(option) => handleFilterChange('FromBranch_Id', option ? option.value : null)}
                                      options={fromBranchOptions}
                                      isClearable
                                      placeholder="Select From Branch..."
                                      isSearchable
                                      styles={selectStyles}
                                      maxMenuHeight={200}
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>From Date</Label>
                                    <DatePicker
                                      selected={filter.FromDate ? new Date(filter.FromDate) : null}
                                      onChange={(date) => handleFilterChange('FromDate', date ? date.toISOString() : null)}
                                      dateFormat="dd/MM/yyyy"
                                      className="form-control"
                                      placeholderText="Select from date..."
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>To Date</Label>
                                    <DatePicker
                                      selected={filter.ToDate ? new Date(filter.ToDate) : null}
                                      onChange={(date) => handleFilterChange('ToDate', date ? date.toISOString() : null)}
                                      dateFormat="dd/MM/yyyy"
                                      className="form-control"
                                      placeholderText="Select to date..."
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <div className="d-flex gap-2">
                                <Button color="primary" onClick={handleApplyFilter}>
                                  Apply Filter
                                </Button>
                                <Button color="warning" onClick={handleClearFilter}>
                                  Clear Filter
                                </Button>
                              </div>
                            </>
                          )}
                        </CardBody>
                      </Card> */}

                      {/* List */}
                      <Card>
                        <CardBody>
                          <h5 className="card-title mb-3">
                            Pending Receipts ({totalCount} records)
                          </h5>

                          {!selectedBranchId ? (
                            <div className="alert alert-info">
                              Please select a branch to view pending receipts.
                            </div>
                          ) : loading ? (
                            <div className="text-center py-4">
                              <ClipLoader size={50} color="#556ee6" />
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table bordered>
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">Sr.No</th>
                                      <th width="15%">From Branch</th>
                                      <th width="15%">Transfer Number</th>
                                      <th width="10%">Total Quantity</th>
                                      <th width="15%">Transfer Date</th>
                                      <th width="20%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedTransfers().length === 0 ? (
                                      <tr>
                                        <td colSpan="6" className="text-center">
                                          No pending receipts found.
                                        </td>
                                      </tr>
                                    ) : (
                                      getPaginatedTransfers().map((transfer, index) => (
                                        <tr key={transfer.stockTransfer_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{transfer.fromBranch_NameEn || 'N/A'}</td>
                                          <td>{transfer.stockTransfer_TransferNo}</td>
                                          <td>{transfer.stockTransfer_TotalQty || 0}</td>
                                          <td>
                                            {transfer.stockTransfer_Date
                                              ? new Date(transfer.stockTransfer_Date).toLocaleDateString()
                                              : 'N/A'}
                                          </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleViewDetails(transfer)}
                                              >
                                                Details
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="success"
                                                  size="sm"
                                                  onClick={() => handleReceive(transfer)}
                                                  disabled={receiveLoadingRow === transfer.stockTransfer_Id}
                                                >
                                                  {receiveLoadingRow === transfer.stockTransfer_Id ? (
                                                    <>
                                                      <ClipLoader size={15} color="white" /> Receiving...
                                                    </>
                                                  ) : (
                                                    <>
                                                      <CheckCircle size={16} className="me-1" />
                                                      Receive
                                                    </>
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === 1}
                                      onClick={() => handlePageChange(page - 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-left"></i>
                                    </Button>

                                    {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                      const pageNum = index + 1;
                                      const isActive = page === pageNum;

                                      return (
                                        <Button
                                          key={pageNum}
                                          color={isActive ? "primary" : "light"}
                                          size="sm"
                                          onClick={() => handlePageChange(pageNum)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          {pageNum}
                                        </Button>
                                      );
                                    })}

                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === totalPages}
                                      onClick={() => handlePageChange(page + 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-right"></i>
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                  <TabPane tabId="2">
                    <div>
                      {/* Branch Selection */}
                      <Row className="mb-4">
                        <Col md={4}>
                          <div className="mb-3">
                            <Label className="fw-bold">Branch <span className="text-danger">*</span></Label>
                            <Select
                              value={selectedBranchId ? branchOptions.find(opt => opt.value === selectedBranchId) : null}
                              onChange={handleBranchChange}
                              options={branchOptions}
                              isClearable={!isBranchReadonly}
                              isDisabled={isBranchReadonly}
                              placeholder="Select Branch..."
                              isSearchable
                              styles={selectStyles}
                              maxMenuHeight={200}
                            />
                            {isBranchReadonly && (
                              <small className="text-muted">Branch is pre-filled from your login</small>
                            )}
                          </div>
                        </Col>
                      </Row>

                      {/* Filters - Commented out for now */}
                      {/* <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">Filter Purchase</h5>
                            <Button
                              color="primary"
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>

                          {showFilters && (
                            <>
                              <Row className="mb-3">
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>Transfer Number</Label>
                                    <Input
                                      type="text"
                                      value={filter.StockTransfer_TransferNo || ''}
                                      onChange={(e) => handleFilterChange('StockTransfer_TransferNo', e.target.value || null)}
                                      placeholder="Enter transfer number..."
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>From Branch</Label>
                                    <Select
                                      value={filter.FromBranch_Id ? fromBranchOptions.find(opt => opt.value === filter.FromBranch_Id) : null}
                                      onChange={(option) => handleFilterChange('FromBranch_Id', option ? option.value : null)}
                                      options={fromBranchOptions}
                                      isClearable
                                      placeholder="Select From Branch..."
                                      isSearchable
                                      styles={selectStyles}
                                      maxMenuHeight={200}
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>From Date</Label>
                                    <DatePicker
                                      selected={filter.FromDate ? new Date(filter.FromDate) : null}
                                      onChange={(date) => handleFilterChange('FromDate', date ? date.toISOString() : null)}
                                      dateFormat="dd/MM/yyyy"
                                      className="form-control"
                                      placeholderText="Select from date..."
                                    />
                                  </div>
                                </Col>
                                <Col md={3}>
                                  <div className="mb-3">
                                    <Label>To Date</Label>
                                    <DatePicker
                                      selected={filter.ToDate ? new Date(filter.ToDate) : null}
                                      onChange={(date) => handleFilterChange('ToDate', date ? date.toISOString() : null)}
                                      dateFormat="dd/MM/yyyy"
                                      className="form-control"
                                      placeholderText="Select to date..."
                                    />
                                  </div>
                                </Col>
                              </Row>
                              <div className="d-flex gap-2">
                                <Button color="primary" onClick={handleApplyFilter}>
                                  Apply Filter
                                </Button>
                                <Button color="warning" onClick={handleClearFilter}>
                                  Clear Filter
                                </Button>
                              </div>
                            </>
                          )}
                        </CardBody>
                      </Card> */}

                      {/* List */}
                      <Card>
                        <CardBody>
                          <h5 className="card-title mb-3">
                            Received ({totalCount} records)
                          </h5>

                          {!selectedBranchId ? (
                            <div className="alert alert-info">
                              Please select a branch to view received transfers.
                            </div>
                          ) : loading ? (
                            <div className="text-center py-4">
                              <ClipLoader size={50} color="#556ee6" />
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table bordered>
                                  <thead className="table-light">
                                    <tr>
                                      <th width="5%">Sr.No</th>
                                      <th width="15%">From Branch</th>
                                      <th width="15%">Transfer Number</th>
                                      <th width="10%">Total Quantity</th>
                                      <th width="15%">Transfer Date</th>
                                      <th width="15%">Received Date</th>
                                      <th width="15%">Status</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {getPaginatedTransfers().length === 0 ? (
                                      <tr>
                                        <td colSpan="8" className="text-center">
                                          No received transfers found.
                                        </td>
                                      </tr>
                                    ) : (
                                      getPaginatedTransfers().map((transfer, index) => (
                                        <tr key={transfer.stockTransfer_Id}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{transfer.fromBranch_NameEn || 'N/A'}</td>
                                          <td>{transfer.stockTransfer_TransferNo}</td>
                                          <td>{transfer.stockTransfer_TotalQty || 0}</td>
                                          <td>
                                            {transfer.stockTransfer_Date
                                              ? new Date(transfer.stockTransfer_Date).toLocaleDateString()
                                              : 'N/A'}
                                          </td>
                                          <td>
                                            {transfer.stockTransfer_ReceivedDate
                                              ? new Date(transfer.stockTransfer_ReceivedDate).toLocaleDateString()
                                              : 'N/A'}
                                          </td>
                                          <td>
                                            <span className="badge bg-success">Received</span>
                                          </td>
                                          <td>
                                            <Button
                                              color="info"
                                              size="sm"
                                              onClick={() => handleViewDetails(transfer)}
                                            >
                                              Details
                                            </Button>
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination */}
                              {totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                  <div>
                                    <p className="text-muted mb-0">
                                      Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                                    </p>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === 1}
                                      onClick={() => handlePageChange(page - 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-left"></i>
                                    </Button>

                                    {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                      const pageNum = index + 1;
                                      const isActive = page === pageNum;

                                      return (
                                        <Button
                                          key={pageNum}
                                          color={isActive ? "primary" : "light"}
                                          size="sm"
                                          onClick={() => handlePageChange(pageNum)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          {pageNum}
                                        </Button>
                                      );
                                    })}

                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === totalPages}
                                      onClick={() => handlePageChange(page + 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-right"></i>
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Details Modal */}
      <Modal isOpen={showDetailsModal} toggle={() => setShowDetailsModal(false)} size="lg">
        <ModalHeader toggle={() => setShowDetailsModal(false)}>
          Transfer Details - {currentTransfer?.stockTransfer_TransferNo}
        </ModalHeader>
        <ModalBody>
          {currentTransfer && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>From Branch:</strong> {currentTransfer.fromBranch_NameEn || 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Transfer Number:</strong> {currentTransfer.stockTransfer_TransferNo}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Transfer Date:</strong> {currentTransfer.stockTransfer_Date ? new Date(currentTransfer.stockTransfer_Date).toLocaleDateString() : 'N/A'}
                </Col>
                <Col md={6}>
                  <strong>Total Quantity:</strong> {currentTransfer.stockTransfer_TotalQty || 0}
                </Col>
              </Row>
              {currentTransfer.stockTransfer_Notes && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Notes:</strong> {currentTransfer.stockTransfer_Notes}
                  </Col>
                </Row>
              )}

              <hr />

              <h6>Products:</h6>
              <div className="table-responsive">
                <Table bordered size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Cost Price</th>
                      <th>Total Amount</th>
                      <th>Stock Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTransfer.details && currentTransfer.details.length > 0 ? (
                      currentTransfer.details.map((detail, idx) => (
                        <tr key={idx}>
                          <td>{detail.product_NameEn || 'N/A'}</td>
                          <td>{detail.stockTransferDet_Quantity || 0}</td>
                          <td>{(detail.stockTransferDet_CostPrice || 0).toFixed(2)}</td>
                          <td>{(detail.stockTransferDet_TotalAmount || 0).toFixed(2)}</td>
                          <td>
                            {detail.stockDetails && detail.stockDetails.length > 0 ? (
                              <Button
                                color="info"
                                size="sm"
                                onClick={() => handleViewStockDetails(
                                  detail.product_NameEn || 'Product',
                                  detail.stockDetails
                                )}
                              >
                                <Package size={16} className="me-1" />
                                View Stocks
                              </Button>
                            ) : (
                              <span className="text-muted">No stocks</span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">No products found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowDetailsModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Stock Details Modal */}
      <StockDetailsModal
        isOpen={showStockDetailsModal}
        toggle={() => setShowStockDetailsModal(false)}
        productName={currentStockDetailsProduct}
        stockDetails={currentStockDetails}
      />
    </div>
  );
}

